// ======================================================
//                   Message Serialization
// ======================================================
//
// A complete message is structured in three layers:
// (outermost to innermost)
//
// ┌────────────────────────────────────────────────────┐
// │                   ENVELOPE (On-chain)              │
// │────────────────────────────────────────────────────│
// │ [0]     u8     : Discriminator                     │
// │ [1..8]  u64    : Spam Mitigation Fee               │
// │ [9..X]  bytes  : Ciphertext (encrypted DataFrame)  │
// │ [X..X+32]      : Ephemeral Public Key              │
// │ [X+32..X+34]   : Shared Secret Hint                │
// │ [X+34..X+40]   : Filter Tag                        │
// └────────────────────────────────────────────────────┘
//
// Location: envelope.ts
//
// - Outer shell of the message
// - Handles fee payment, filtering, and key discovery
//
//
// ┌────────────────────────────────────────────────────┐
// │                 DATA FRAME (Encrypted)             │
// │────────────────────────────────────────────────────│
// │ [0..2]   u16    : Payload Length                   │
// │ [2..18]  bytes  : Conversation ID                  │
// │ [18..50] bytes  : Sender HMAC Proof                │
// │ [50..82] bytes  : Sender Next View Public Key      │
// │ [82..114]bytes  : Sender Current View Public Key   │
// │ [114..X] bytes  : Payload (padded with random)     │
// └────────────────────────────────────────────────────┘
//
// Location: dataFrame.ts
//
// - Encrypted message content
// - Contains sender identity, conversation state
//
//
// ┌────────────────────────────────────────────────────┐
// │                CHUNK / MESSAGE CONTENT             │
// │────────────────────────────────────────────────────│
// │ [0]     u8     : Version                           │
// │ [1..9]  bytes  : Message ID                        │
// │ [9..11] u16    : Fragment Index                    │
// │ [11..13]u16    : Total Fragments                   │
// │ [13]    u8     : Message Type                      │
// │ [14..22]bytes  : Client ID                         │
// │ [22..N] bytes  : Message Payload                   │
// └────────────────────────────────────────────────────┘
//
// Location: chunk.ts
//
// - User-level message content
// - Allows chunking, typing, client info

/**
 * === Envelope Layer Constants ===
 *
 * These constants define the outer structure of an Unlink protocol message instruction.
 * They include protocol discriminators, fee metadata, and message routing hints.
 * This layer wraps and identifies encrypted payloads.
 */

// Length (in bytes) of the discriminator prefix used to identify instruction variants.
// All instructions begin with this discriminator.
export const DISCRIMINATOR_LENGTH = 1;

// Discriminator: 0x00 → Message submission instruction.
// Transfers lamports to the Unlink Foundation account.
export const DISCRIMINATOR_MESSAGE = 0x00;

// Discriminator: 0x01 → Update minimum fee (restricted to foundation authority).
export const DISCRIMINATOR_UPDATE_MIN_FEE = 0x01;

// PDA seed used to store the current minimum required contribution.
export const MINIMUM_FEE_PDA_SEED = 'minimum_contribution';

// Length (in bytes) of the spam mitigation fee field (u64 LE).
// Included in every message instruction to enforce economic cost.
export const FEE_LENGTH = 8;

// Length (in bytes) of the short hint derived from the ECDH shared secret.
// Used for efficient recipient-side filtering without full decryption.
export const HINT_LENGTH = 2;

// Length (in bytes) of the filter tag's hashed data (max 40 bits, left-aligned).
export const FILTER_TAG_DATA_LENGTH = 5;

// Total length (in bytes) of the filter tag structure:
// - 1 byte: bit-length prefix (1–40)
// - 5 bytes: hashed tag data (left-aligned)
export const FILTER_TAG_TOTAL_LENGTH = 6;

/**
 * Total length (in bytes) of all envelope fields prepended to the ciphertext:
 * - 1 byte: discriminator
 * - 8 bytes: fee
 * - 2 bytes: hint
 * - 6 bytes: filter tag
 */
export const TOTAL_LENGTH =
  DISCRIMINATOR_LENGTH +
  FEE_LENGTH +
  HINT_LENGTH +
  FILTER_TAG_TOTAL_LENGTH;